import React, { useState } from 'react';
import axios from 'axios';
import { Lock, CheckCircle2, Heart, Star, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LockScreen({ blockingItem, pet, onUnlock }) {
  const [completing, setCompleting] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.patch(
        `${API}/schedule/${blockingItem.id}`,
        { completed_today: true },
        { withCredentials: true }
      );
      setShowConfetti(true);
      toast.success(`+${blockingItem.points} points! ${pet?.name || 'Your pet'} is proud of you!`, { icon: '🎉' });
      
      // Show confetti briefly then unlock
      setTimeout(() => {
        setShowConfetti(false);
        if (onUnlock) onUnlock();
      }, 2000);
    } catch (e) {
      toast.error('Something went wrong');
      setCompleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-[#FFFAF0]"
      data-testid="lock-screen"
    >
      {/* Confetti burst */}
      {showConfetti && (
        <div className="fixed inset-0 z-[1001] pointer-events-none overflow-hidden">
          {Array.from({ length: 60 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-sm"
              style={{
                width: `${8 + Math.random() * 12}px`,
                height: `${8 + Math.random() * 12}px`,
                background: ['#A2D2FF', '#FFC8DD', '#B9FBC0', '#CDB4DB', '#FDF0D5', '#FF6B6B'][i % 6],
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                animation: `confetti-fall ${1.5 + Math.random() * 2}s ease-in forwards`,
                animationDelay: `${Math.random() * 0.5}s`,
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
          ))}
          <style>{`
            @keyframes confetti-fall {
              0% { top: -20px; opacity: 1; transform: rotate(0deg) translateX(0); }
              100% { top: 110vh; opacity: 0; transform: rotate(720deg) translateX(${Math.random() > 0.5 ? '' : '-'}80px); }
            }
          `}</style>
        </div>
      )}

      <div className="max-w-lg w-full mx-4 text-center">
        {/* Lock icon */}
        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#FFC8DD] border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] mb-6 animate-pulse-soft">
          <Lock className="w-12 h-12 text-black" strokeWidth={2.5} />
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold font-['Fredoka'] text-gray-900 mb-3">
          Hey! It's Time!
        </h1>

        {/* Blocking Task Card */}
        <div className="neo-card p-8 mb-6 bg-[#FDF0D5]">
          <p className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-2">
            Scheduled at {blockingItem.scheduled_time}
          </p>
          <h2 className="text-3xl font-bold font-['Fredoka'] text-gray-900 mb-3">
            {blockingItem.title}
          </h2>
          {blockingItem.description && (
            <p className="text-lg text-gray-600 mb-4">{blockingItem.description}</p>
          )}
          
          {/* Points badge */}
          <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full border-2 border-black">
            <Star className="w-5 h-5 text-yellow-600" fill="currentColor" />
            <span className="font-bold font-['Fredoka']">{blockingItem.points} points waiting for you!</span>
          </div>
        </div>

        {/* Pet encouragement */}
        {pet && (
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-full border-4 border-black overflow-hidden shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] animate-wiggle">
              <img src={pet.image} alt={pet.name} className="w-full h-full object-cover" />
            </div>
            <div className="neo-card p-4 bg-white text-left max-w-xs">
              <p className="font-semibold">
                {pet.name} says: <em>"I believe in you! Do this and I'll be so happy!"</em>
              </p>
              <div className="flex items-center gap-1 mt-1 text-sm text-gray-500">
                <Heart className="w-3 h-3 text-red-400" fill="currentColor" />
                <span>Your pet is watching</span>
              </div>
            </div>
          </div>
        )}

        {/* Complete button */}
        {!showConfetti && (
          <button
            onClick={handleComplete}
            disabled={completing}
            className="neo-btn px-10 py-5 bg-[#B9FBC0] text-xl flex items-center justify-center gap-3 mx-auto"
            data-testid="lock-complete-btn"
          >
            {completing ? (
              <span className="animate-pulse">Completing...</span>
            ) : (
              <>
                <CheckCircle2 className="w-7 h-7" />
                <span>I Did It!</span>
              </>
            )}
          </button>
        )}

        {showConfetti && (
          <div className="text-center animate-bounce-in">
            <div className="inline-flex items-center gap-2 text-2xl font-bold font-['Fredoka'] text-green-700">
              <Sparkles className="w-8 h-8" />
              Amazing work!
              <Sparkles className="w-8 h-8" />
            </div>
          </div>
        )}

        {/* Subtle message */}
        <p className="mt-6 text-sm text-gray-400 font-semibold">
          App is locked until this task is complete
        </p>
      </div>
    </div>
  );
}
